import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { AccountOption } from './account-select-panel';

/**
 * Whether a set of accounts spans more than the organization's currency:
 * true when any ACTIVE account carries a currency other than
 * `defaultCurrency` (an account with no currency is in the org currency).
 * This is THE rule behind showing currencies in the account pickers.
 *
 * Retired accounts do not count (Clay, 2026-09-17): the pickers only ever
 * list active accounts, so a deactivated USD bank must not switch currency
 * labels on across a EUR-only chart. An account with no `is_active` field
 * is treated as active.
 *
 * Callers that hand a picker a FILTERED list (liabilities only, bank only)
 * should evaluate this over the whole chart — `useShowAccountCurrency` does
 * exactly that — and pass the result as the picker's `showCurrency`.
 * Otherwise a USD-only liability list next to a EUR + USD pay-from list
 * shows no currency on the liability side, which is exactly when the user
 * needs it (paying down a liability from assets in several currencies).
 */
export function chartHasMultipleCurrencies(
  accounts: ReadonlyArray<Pick<AccountOption, 'currency'> & { is_active?: boolean | null }>,
  defaultCurrency: string | null | undefined,
): boolean {
  if (!defaultCurrency) return false;
  return accounts.some(
    (acc) => acc.is_active !== false && !!acc.currency && acc.currency !== defaultCurrency,
  );
}

/**
 * The `showCurrency` value for pickers that receive a filtered slice of the
 * chart: `chartHasMultipleCurrencies` over the WHOLE chart the caller loaded,
 * against the org currency. One hook so the six money-movement forms share
 * the rule and its explanation instead of each restating it.
 */
export function useShowAccountCurrency(
  chart: ReadonlyArray<Pick<AccountOption, 'currency'> & { is_active?: boolean | null }> | null | undefined,
): boolean {
  const { selectedOrganization } = useOrganization();
  const defaultCurrency = selectedOrganization?.currency;
  return React.useMemo(
    () => chartHasMultipleCurrencies(chart ?? [], defaultCurrency),
    [chart, defaultCurrency],
  );
}

export interface AccountLabelOptions {
  /**
   * Force the currency onto every type label (falling back to the org
   * currency for accounts with none). `undefined` keeps the default rule:
   * currencies show when the given list itself spans more than one.
   * `false` hides them even when the list spans several — pass `undefined`,
   * not `false`, to keep the list rule.
   */
  showCurrency?: boolean;
}

/**
 * Returns the same `getDisplayName` and `buildTypeLabel` helpers that
 * `AccountSelectPanel` uses internally for its dropdown items. Use these from
 * a parent's trigger so the selected-state label matches the dropdown rows.
 */
export function useAccountLabelHelpers(accounts: AccountOption[], options?: AccountLabelOptions) {
  const { selectedOrganization } = useOrganization();
  const { t } = useTranslation();
  const defaultCurrency = selectedOrganization?.currency;
  const showCurrencyOverride = options?.showCurrency;

  const getDisplayName = React.useCallback((account: AccountOption) => {
    if (account.system_account_type) {
      return t(`accounts:systemAccounts.${account.system_account_type}`, account.account_name);
    }
    return account.account_name;
  }, [t]);

  const showCurrency = React.useMemo(
    () => showCurrencyOverride ?? chartHasMultipleCurrencies(accounts, defaultCurrency),
    [showCurrencyOverride, accounts, defaultCurrency],
  );

  const buildTypeLabel = React.useCallback((account: AccountOption): string => {
    const typeName = account.type?.type_name || '';
    if (!showCurrency) return typeName;
    const currency = account.currency || defaultCurrency;
    if (currency) return typeName ? `${typeName} (${currency})` : currency;
    return typeName;
  }, [showCurrency, defaultCurrency]);

  return { getDisplayName, buildTypeLabel };
}
